import {
  schoolDistrictGetActiveSchoolVisitorSignInsQuery,
  schoolDistrictGetSchoolSignInsQuery,
  type UserDistrictSummary,
  type UserDto,
  useSchoolDistrictGetParents,
  useSchoolDistrictSignInUser,
  useSchoolDistrictSignOutUser,
} from "@crowdcoursing/api/openapi";
import { Box, Typography } from "@mui/material";
import { type QueryClient, skipToken, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { useToast } from "~hooks/useToast";
import { VisitorManagementActionCards } from "~ui/organisms/VisitorManagementActionCards/VisitorManagementActionCards";
import { VisitorManagementFacultyDialog } from "~ui/organisms/VisitorManagementFacultyDialog/VisitorManagementFacultyDialog";
import { VisitorManagementStudentDialog } from "~ui/organisms/VisitorManagementStudentDialog/VisitorManagementStudentDialog";

import { useBubbleNonStudentUsers, useBubbleStudentUsers } from "../AdminPage/schoolUserManagementHooks";
import {
  facultyMatchesQuery,
  parentUserDtoDisplayNameLastFirst,
  sortFacultyByName,
  sortStudentsByName,
  studentMatchesQuery,
} from "./visitorManagementDisplay";

const tFacultySignInSuccess = "Signed in.";
const tFacultySignInError = "Could not sign in.";
const tStudentSignInSuccess = "Student signed in.";
const tStudentSignInError = "Could not sign in student.";
const tStudentSignOutSuccess = "Student signed out.";
const tStudentSignOutError = "Could not sign out student.";
const tSignOutSuccess = "Signed out.";
const tSignOutError = "Could not sign out.";

/** Prefix key matches all `useSchoolDistrictGetSchoolSignIns` variants (any date range). */
async function invalidateVisitorManagementQueries(
  queryClient: QueryClient,
  bubbleId: string,
  options: { schoolSignIns: boolean },
) {
  const tasks = [
    queryClient.invalidateQueries({
      queryKey: schoolDistrictGetActiveSchoolVisitorSignInsQuery({ pathParams: { bubbleId } }).queryKey,
    }),
  ];
  if (options.schoolSignIns) {
    tasks.push(
      queryClient.invalidateQueries({
        queryKey: schoolDistrictGetSchoolSignInsQuery({ pathParams: { bubbleId } }).queryKey,
      }),
    );
  }
  await Promise.all(tasks);
}

export interface VisitorManagementVisitorActionsProps {
  /** Default: home page cards. Kiosk: large touch targets, full-screen dialogs. */
  density?: "default" | "kiosk";
}

export function VisitorManagementVisitorActions({ density = "default" }: VisitorManagementVisitorActionsProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { schoolId } = useParams<{ schoolId: string }>();
  const isKiosk = density === "kiosk";
  const { status: facultyStatus, users: facultyUsers } = useBubbleNonStudentUsers(schoolId ?? "");
  const { students: studentUsers, status: studentUsersStatus } = useBubbleStudentUsers(schoolId ?? "");
  const { isPending: isSignInPending, mutateAsync: signInUser } = useSchoolDistrictSignInUser();
  const { isPending: isSignOutPending, mutateAsync: signOutUser } = useSchoolDistrictSignOutUser();
  const [facultyModalOpen, setFacultyModalOpen] = useState(false);
  const [facultySearch, setFacultySearch] = useState("");
  const [facultyForSignIn, setFacultyForSignIn] = useState<UserDistrictSummary | null>(null);
  const [studentModalOpen, setStudentModalOpen] = useState(false);
  const [studentSearch, setStudentSearch] = useState("");
  const [studentForAction, setStudentForAction] = useState<UserDistrictSummary | null>(null);
  const [studentSignOutOptionsOpen, setStudentSignOutOptionsOpen] = useState(false);

  const { data: studentParentUsers = [], status: studentParentsStatus } = useSchoolDistrictGetParents(
    schoolId && studentForAction?.userId && studentSignOutOptionsOpen
      ? { pathParams: { bubbleId: schoolId, userId: studentForAction.userId } }
      : skipToken,
  );

  const parentOptionsForStudent = useMemo<{ id: string; name: string }[]>(() => {
    return studentParentUsers
      .filter((p): p is UserDto & { id: string } => Boolean(p.id))
      .map((p) => ({ id: p.id, name: parentUserDtoDisplayNameLastFirst(p) }));
  }, [studentParentUsers]);

  const sortedFaculty = useMemo(() => [...facultyUsers].sort(sortFacultyByName), [facultyUsers]);

  const filteredFaculty = useMemo(
    () => sortedFaculty.filter((u) => facultyMatchesQuery(u, facultySearch)),
    [sortedFaculty, facultySearch],
  );

  const sortedStudents = useMemo(() => [...studentUsers].sort(sortStudentsByName), [studentUsers]);

  const filteredStudents = useMemo(
    () => sortedStudents.filter((u) => studentMatchesQuery(u, studentSearch)),
    [sortedStudents, studentSearch],
  );

  const facultySearchActive = facultySearch.trim().length >= 1;
  const studentSearchActive = studentSearch.trim().length >= 1;

  const closeFacultyModal = () => {
    setFacultyModalOpen(false);
    setFacultySearch("");
    setFacultyForSignIn(null);
  };

  const closeStudentModal = () => {
    setStudentModalOpen(false);
    setStudentSearch("");
    setStudentForAction(null);
    setStudentSignOutOptionsOpen(false);
  };

  const handleFacultySignIn = async () => {
    const userId = facultyForSignIn?.userId;
    if (!schoolId || !userId) {
      return;
    }
    try {
      await signInUser({ pathParams: { bubbleId: schoolId, userId } });
      await invalidateVisitorManagementQueries(queryClient, schoolId, { schoolSignIns: true });
      toast.success(tFacultySignInSuccess);
      closeFacultyModal();
    } catch {
      toast.error(tFacultySignInError);
    }
  };

  const handleFacultySignOut = async () => {
    const userId = facultyForSignIn?.userId;
    if (!schoolId || !userId) {
      return;
    }
    try {
      await signOutUser({
        pathParams: { bubbleId: schoolId, userId },
        queryParams: { parentId: userId },
      });
      await invalidateVisitorManagementQueries(queryClient, schoolId, { schoolSignIns: false });
      toast.success(tSignOutSuccess);
      closeFacultyModal();
    } catch {
      toast.error(tSignOutError);
    }
  };

  const handleStudentSignIn = async () => {
    const userId = studentForAction?.userId;
    if (!schoolId || !userId) {
      return;
    }
    try {
      await signInUser({ pathParams: { bubbleId: schoolId, userId } });
      await invalidateVisitorManagementQueries(queryClient, schoolId, { schoolSignIns: true });
      toast.success(tStudentSignInSuccess);
      closeStudentModal();
    } catch {
      toast.error(tStudentSignInError);
    }
  };

  const handleSignOutStudent = async (parentUserIdForQuery?: string) => {
    const userId = studentForAction?.userId;
    if (!schoolId || !userId) {
      return;
    }
    try {
      const parentId = parentUserIdForQuery ?? userId;
      await signOutUser({
        pathParams: { bubbleId: schoolId, userId },
        queryParams: { parentId },
      });
      await invalidateVisitorManagementQueries(queryClient, schoolId, { schoolSignIns: false });
      toast.success(tStudentSignOutSuccess);
      closeStudentModal();
    } catch {
      toast.error(tStudentSignOutError);
    }
  };

  if (!schoolId) {
    return (
      <Typography color="text.secondary" variant="body1">
        Missing school.
      </Typography>
    );
  }

  const dialogFullScreen = isKiosk;
  const listItemSx = isKiosk
    ? { columnGap: 3, minHeight: 72, py: 2.5 }
    : {
        columnGap: 3,
        py: 1.5,
      };
  const listAvatarSize = isKiosk ? 56 : 48;
  const dialogTitleTypography = isKiosk ? "h5" : "h6";
  const dialogButtonSize = isKiosk ? "large" : "medium";
  const dialogMaxWidth = isKiosk ? "md" : "sm";

  const cards = (
    <VisitorManagementActionCards
      isKiosk={isKiosk}
      onFacultyCardClick={() => {
        setFacultyModalOpen(true);
        setFacultySearch("");
        setFacultyForSignIn(null);
      }}
      onStudentCardClick={() => {
        setStudentModalOpen(true);
        setStudentSearch("");
        setStudentForAction(null);
        setStudentSignOutOptionsOpen(false);
      }}
    />
  );

  return (
    <>
      {isKiosk ? (
        <Box sx={{ display: "flex", flex: "1 1 auto", flexDirection: "column", minHeight: 0, width: "100%" }}>
          {cards}
        </Box>
      ) : (
        cards
      )}

      <VisitorManagementFacultyDialog
        closeFacultyModal={closeFacultyModal}
        dialogButtonSize={dialogButtonSize}
        dialogFullScreen={dialogFullScreen}
        dialogTitleTypography={dialogTitleTypography}
        facultyForSignIn={facultyForSignIn}
        facultyModalOpen={facultyModalOpen}
        facultySearch={facultySearch}
        facultySearchActive={facultySearchActive}
        facultyStatus={facultyStatus}
        filteredFaculty={filteredFaculty}
        handleFacultySignIn={handleFacultySignIn}
        handleFacultySignOut={handleFacultySignOut}
        isKiosk={isKiosk}
        isSignInPending={isSignInPending}
        isSignOutPending={isSignOutPending}
        listAvatarSize={listAvatarSize}
        listItemSx={listItemSx}
        maxWidth={dialogMaxWidth}
        setFacultyForSignIn={setFacultyForSignIn}
        setFacultySearch={setFacultySearch}
        sortedFaculty={sortedFaculty}
      />

      <VisitorManagementStudentDialog
        closeStudentModal={closeStudentModal}
        dialogButtonSize={dialogButtonSize}
        dialogFullScreen={dialogFullScreen}
        dialogTitleTypography={dialogTitleTypography}
        filteredStudents={filteredStudents}
        handleSignOutStudent={handleSignOutStudent}
        handleStudentSignIn={handleStudentSignIn}
        isKiosk={isKiosk}
        isSignInPending={isSignInPending}
        isSignOutPending={isSignOutPending}
        listAvatarSize={listAvatarSize}
        listItemSx={listItemSx}
        maxWidth={dialogMaxWidth}
        parentOptionsForStudent={parentOptionsForStudent}
        setStudentForAction={setStudentForAction}
        setStudentSearch={setStudentSearch}
        setStudentSignOutOptionsOpen={setStudentSignOutOptionsOpen}
        sortedStudents={sortedStudents}
        studentForAction={studentForAction}
        studentModalOpen={studentModalOpen}
        studentParentsStatus={studentParentsStatus}
        studentSearch={studentSearch}
        studentSearchActive={studentSearchActive}
        studentSignOutOptionsOpen={studentSignOutOptionsOpen}
        studentUsersStatus={studentUsersStatus}
      />
    </>
  );
}
